import type { RegistrationUpdateFieldsInput } from '@/types/composition-functions';

/**
 * The editable shape of the course-registration edit dialog. Kept independent of
 * the backend input type so the diffing logic can be unit-tested in isolation.
 */
export interface CourseRegEditForm {
  academic_calendar_id: string | null;
  semester: number | null;
  registration_level: string | null;
  programme_id: string | null;
  approval_status: string | null;
  senate_approval_status: string | null;
}

/**
 * Build the mutation payload from ONLY the fields the user actually changed,
 * comparing the current dialog values against the values it opened with.
 *
 * This is the fix for #357: the dialog prefills every field, so submitting the
 * whole form re-sent an untouched `approval_status`. When that value is a legacy
 * Title-case status (e.g. "Approved") the tightened backend enum rejects it with
 * a 422 — even though the user only meant to change an unrelated field. Sending
 * just the changed fields means untouched legacy values are never transmitted,
 * while a deliberate status change is still sent through.
 */
export function buildChangedRegistrationFields(
  current: CourseRegEditForm,
  original: CourseRegEditForm,
): RegistrationUpdateFieldsInput {
  const fields: RegistrationUpdateFieldsInput = {};

  if (current.academic_calendar_id && current.academic_calendar_id !== original.academic_calendar_id) {
    const parsed = parseInt(current.academic_calendar_id, 10);
    if (Number.isFinite(parsed)) fields.academic_calendar_id = parsed;
  }
  if (current.semester !== null && current.semester !== original.semester) {
    fields.semester = current.semester;
  }
  if (current.registration_level !== null && current.registration_level !== original.registration_level) {
    fields.registration_level = current.registration_level;
  }
  if (current.programme_id && current.programme_id !== original.programme_id) {
    const parsed = parseInt(current.programme_id, 10);
    if (Number.isFinite(parsed)) fields.programme_id = parsed;
  }
  if (current.approval_status && current.approval_status !== original.approval_status) {
    fields.approval_status = current.approval_status;
  }
  if (current.senate_approval_status !== null && current.senate_approval_status !== original.senate_approval_status) {
    fields.senate_approval_status = current.senate_approval_status;
  }

  return fields;
}
